import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  BookUser,
  CheckCircle2,
  ChevronDown,
  Coins,
  FileSpreadsheet,
  FileText,
  Loader2,
  Upload,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  DELIVERY_IMPORT_FIELDS,
  IMPORT_DELIVERIES_BATCH_SIZE,
  isFreeMessagesOfferActive,
  TEMPLATE_SEND_COST,
  type DeliveryImportField,
  type DeliveryImportRowResultDto,
  type ImportDeliveriesResultDto,
  type ImportDeliveryRowInput,
  type TemplateDto,
} from "@mandoob/shared";

import { useImportDeliveries } from "@/api/inbox";
import { useGetTemplates } from "@/api/templates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage, type TranslateFn } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { errorMessage, messageForErrorCode } from "@/lib/errors";
import { formatPhoneNumberForDisplay } from "@/lib/phone-utils";
import { cn } from "@/lib/utils";
import { loadSession, useSession } from "@/session";

import {
  groupTemplateVariables,
  type FieldKey as TemplateFieldKey,
} from "./template-fields";
import { formatTemplateName } from "./lib";
import {
  ContactsPermissionError,
  contactsSourceAvailable,
  pickDeviceContacts,
  takeStashedContacts,
  type PickedContact,
} from "./import/contacts";
import { ContactsPickerDialog } from "./import/contacts-picker-dialog";
import {
  buildImportRows,
  guessColumnMapping,
  guessVariableMapping,
  type ColumnMapping,
  type InvalidRow,
  type InvalidRowReason,
  type VariableMapping,
} from "./import/mapping";
import {
  parseSpreadsheet,
  SPREADSHEET_ACCEPT,
  SpreadsheetError,
  type SheetData,
} from "./import/spreadsheet";

type Step = "file" | "template" | "map" | "run" | "done";

/** Sentinel select values — Radix forbids an empty-string item value. */
const UNMAPPED = "__none__";
const FIXED = "__fixed__";

/** Column-matching labels reuse the single-delivery form labels. */
const FIELD_LABEL_KEYS: Record<DeliveryImportField, string> = {
  clientPhone: "Mandoob:NewDelivery.ClientPhone",
  clientName: "Mandoob:NewDelivery.ClientName",
  trackingNumber: "Mandoob:Delivery.TrackingNumber",
  company: "Mandoob:Delivery.Company",
  driverName: "Mandoob:Delivery.DriverName",
  driverCallingPhone: "Mandoob:Delivery.CallingPhone",
  paymentType: "Mandoob:Import.FieldPaymentType",
  cost: "Mandoob:Import.FieldCost",
  shippingCost: "Mandoob:Import.FieldShippingCost",
  senderName: "Mandoob:Import.FieldSenderName",
  senderWebsite: "Mandoob:Import.FieldSenderWebsite",
  weight: "Mandoob:Import.FieldWeight",
  nationalAddress: "Mandoob:Import.FieldNationalAddress",
  clientCity: "Mandoob:Import.FieldClientCity",
};

// The core delivery fields always show in the matching step; the extra
// shipment facts sit behind a disclosure that opens by itself only when the
// file's headers matched some of them — so they are never a prompt.
const CORE_FIELDS = DELIVERY_IMPORT_FIELDS.filter((field) => !field.extra);
const EXTRA_FIELDS = DELIVERY_IMPORT_FIELDS.filter((field) => field.extra);

/** Template field key -> the delivery import column carrying the same data. */
const IMPORT_FIELD_BY_TEMPLATE_FIELD: Record<
  TemplateFieldKey,
  DeliveryImportField
> = {
  clientName: "clientName",
  trackingNumber: "trackingNumber",
  company: "company",
  driverName: "driverName",
  driverPhone: "driverCallingPhone",
};

/**
 * Bulk delivery import: pick a CSV/Excel file, match its columns to the
 * delivery fields and the template variables, then send. Parsing and
 * validation happen in the browser — the file itself is never uploaded, only
 * the rows that passed.
 */
export function ImportDeliveriesPage({
  initialSource,
}: {
  /** "contacts" (from ?source=) opens the contacts picker immediately. */
  initialSource?: "contacts";
}) {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const session = useSession();
  const credits = session?.driver.creditsBalance ?? 0;

  const [step, setStep] = useState<Step>("file");
  const [parsing, setParsing] = useState(false);
  const [sheet, setSheet] = useState<SheetData | null>(null);
  const [template, setTemplate] = useState<TemplateDto | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [variables, setVariables] = useState<VariableMapping>({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ImportDeliveriesResultDto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const templatesQuery = useGetTemplates(
    { MaxResultCount: 100 },
    { query: { enabled: step === "template" } },
  );
  const templates = (templatesQuery.data?.items ?? []).filter(
    (tpl) => !tpl.hasMediaHeader && tpl.status === "Approved",
  );

  const importDeliveries = useImportDeliveries();

  // Re-validated on every mapping change so the ready/skipped counts and the
  // preview always describe what pressing Import would actually send.
  const built = useMemo(
    () =>
      sheet
        ? buildImportRows(sheet, mapping, variables)
        : { valid: [], invalid: [] },
    [sheet, mapping, variables],
  );

  const variableNames = template?.variables ?? [];
  const unmappedVariables = variableNames.filter((name) => !variables[name]);
  const extraMatched = EXTRA_FIELDS.filter(
    (field) => mapping[field.key] !== undefined,
  ).length;
  const canImport =
    !!template && built.valid.length > 0 && unmappedVariables.length === 0;

  const reset = () => {
    setStep("file");
    setSheet(null);
    setTemplate(null);
    setMapping({});
    setVariables({});
    setResult(null);
    setProgress({ done: 0, total: 0 });
  };

  // Contacts source: the whole book (native) waits in `deviceContacts` for
  // the selection dialog; the web picker returns an already-picked list.
  const [pickingContacts, setPickingContacts] = useState(false);
  const [deviceContacts, setDeviceContacts] = useState<PickedContact[]>([]);
  const [contactsPickerOpen, setContactsPickerOpen] = useState(false);

  /** Selected contacts become a two-column sheet feeding the normal flow. */
  const applyContacts = (contacts: PickedContact[]) => {
    setContactsPickerOpen(false);
    if (contacts.length === 0) return;
    setSheet({
      fileName: t("Mandoob:Import.ContactsSource"),
      headers: [
        t("Mandoob:NewDelivery.ClientName"),
        t("Mandoob:NewDelivery.ClientPhone"),
      ],
      rows: contacts.map((contact, index) => ({
        rowNumber: index + 1,
        cells: [contact.name, contact.phone],
      })),
    });
    setMapping({ clientName: 0, clientPhone: 1 });
    setTemplate(null);
    setVariables({});
    setStep("template");
  };

  const handlePickContacts = async () => {
    setPickingContacts(true);
    try {
      const result = await pickDeviceContacts();
      if (!result) return;
      if (result.contacts.length === 0) {
        toast.error(t("Mandoob:Import.ContactsEmpty"));
        return;
      }
      if (result.mode === "picked") {
        applyContacts(result.contacts);
      } else {
        setDeviceContacts(result.contacts);
        setContactsPickerOpen(true);
      }
    } catch (error) {
      toast.error(
        error instanceof ContactsPermissionError
          ? t("Mandoob:Import.ContactsPermission")
          : errorMessage(error, t),
      );
    } finally {
      setPickingContacts(false);
    }
  };

  // Arriving via the new-delivery "from contacts" tile: that page already ran
  // the picker and stashed the selection — consume it and jump to the template
  // step. A direct ?source=contacts deep-link (no stash) opens the picker here
  // instead. The ref guards StrictMode's double effect.
  const autoPickedRef = useRef(false);
  useEffect(() => {
    if (initialSource !== "contacts" || autoPickedRef.current) return;
    autoPickedRef.current = true;
    const stashed = takeStashedContacts();
    if (stashed?.length) {
      applyContacts(stashed);
      return;
    }
    if (contactsSourceAvailable()) void handlePickContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSource]);

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      const data = await parseSpreadsheet(file);
      setSheet(data);
      setMapping(guessColumnMapping(data.headers));
      setTemplate(null);
      setVariables({});
      setStep("template");
    } catch (error) {
      toast.error(
        error instanceof SpreadsheetError && error.reason === "empty"
          ? t("Mandoob:Import.EmptyFile")
          : t("Mandoob:Import.UnsupportedFile"),
      );
    } finally {
      setParsing(false);
    }
  };

  const handleTemplate = (tpl: TemplateDto) => {
    setTemplate(tpl);
    const guessed = sheet
      ? guessVariableMapping(tpl.variables, sheet.headers, mapping)
      : {};
    // Grouped variables ({ar|en}_field pairs) share ONE control: spread any
    // guess across the whole group, and default field-convention groups from
    // the delivery-column mapping above (ar_name/en_name -> the client-name
    // column, etc.) so most of them arrive pre-filled.
    for (const group of groupTemplateVariables(tpl.variables)) {
      let source = group.names
        .map((name) => guessed[name])
        .find((value) => value !== undefined);
      if (!source && group.field) {
        const importKey = IMPORT_FIELD_BY_TEMPLATE_FIELD[group.field];
        const column = importKey !== undefined ? mapping[importKey] : undefined;
        if (column !== undefined) source = { kind: "column", column };
      }
      if (source) {
        for (const name of group.names) guessed[name] = source;
      }
    }
    setVariables(guessed);
    setStep("map");
  };

  const setField = (field: DeliveryImportField, value: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (value === UNMAPPED) delete next[field];
      else next[field] = Number(value);
      return next;
    });
  };

  /** One control drives every variable in a group (both language variants). */
  const setVariableGroup = (names: string[], value: string) => {
    setVariables((prev) => {
      const next = { ...prev };
      for (const name of names) {
        if (value === UNMAPPED) delete next[name];
        else if (value === FIXED) next[name] = { kind: "fixed", value: "" };
        else next[name] = { kind: "column", column: Number(value) };
      }
      return next;
    });
  };

  /**
   * Rows go up in batches so a long file cannot time out a single request and
   * the driver sees progress. The server stops sending the moment credits run
   * out, so the first batch reporting skipped rows ends the run and the
   * untouched remainder is reported as skipped too.
   */
  const runImport = async () => {
    if (!template || !canImport) return;
    const rows = built.valid;
    setStep("run");
    setProgress({ done: 0, total: rows.length });

    const results: DeliveryImportRowResultDto[] = [];
    let stopCode: string | null = null;
    try {
      for (let i = 0; i < rows.length; i += IMPORT_DELIVERIES_BATCH_SIZE) {
        const batch = rows.slice(i, i + IMPORT_DELIVERIES_BATCH_SIZE);
        const batchResult = await importDeliveries.mutateAsync({
          templateId: template.id,
          rows: batch,
        });
        results.push(...batchResult.results);
        setProgress({ done: results.length, total: rows.length });
        if (batchResult.skipped > 0) {
          stopCode = "Credits:Insufficient";
          break;
        }
      }
    } catch (error) {
      toast.error(errorMessage(error, t));
    }

    setResult(summarize(rows, results, stopCode));
    setStep("done");
    // The batch debited credits — refresh the balance shown across the app.
    void loadSession().catch(() => undefined);
  };

  return (
    <div dir={dir} className="tw:flex tw:h-full tw:flex-col">
      <header className="tw:shrink-0 tw:border-b tw:border-border">
        <div className="tw:mx-auto tw:w-full tw:flex tw:items-center tw:gap-3 tw:px-3 tw:pb-3 tw:pt-[calc(12px+env(safe-area-inset-top))] tw:md:max-w-3xl">
          <button
            type="button"
            onClick={() => void navigate({ to: "/chats/new" })}
            disabled={step === "run"}
            aria-label={t("Mandoob:Back")}
            className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted tw:disabled:opacity-40"
          >
            <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
          </button>
          <h1 className="tw:m-0 tw:text-lg tw:font-semibold">
            {step === "done"
              ? t("Mandoob:Import.DoneTitle")
              : t("Mandoob:Import.Title")}
          </h1>
        </div>
      </header>

      <div className="tw:mx-auto tw:min-h-0 tw:w-full tw:flex-1 tw:overflow-y-auto tw:p-4 tw:md:max-w-3xl tw:md:p-6">
        {step === "file" && (
          <FileStep
            parsing={parsing}
            onPick={() => fileInput.current?.click()}
            pickingContacts={pickingContacts}
            contactsAvailable={contactsSourceAvailable()}
            onPickContacts={() => void handlePickContacts()}
          />
        )}

        {step === "template" && (
          <TemplateStep
            loading={templatesQuery.isLoading}
            templates={templates}
            sheet={sheet}
            onSelect={handleTemplate}
          />
        )}

        {step === "map" && sheet && (
          <div className="tw:flex tw:flex-col tw:gap-5">
            <FileSummary
              sheet={sheet}
              template={template}
              onChangeFile={() => fileInput.current?.click()}
              onChangeTemplate={() => setStep("template")}
            />

            <section className="tw:flex tw:flex-col tw:gap-3">
              <div>
                <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
                  {t("Mandoob:Import.MatchColumns")}
                </h2>
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {t("Mandoob:Import.MatchColumnsHint")}
                </p>
              </div>
              {CORE_FIELDS.map((field) => (
                <div key={field.key} className="tw:flex tw:flex-col tw:gap-1.5">
                  <Label className="tw:flex tw:items-center tw:gap-1">
                    {t(FIELD_LABEL_KEYS[field.key])}
                    <span className="tw:text-xs tw:font-normal tw:text-muted-foreground">
                      (
                      {field.required
                        ? t("Mandoob:Import.Required")
                        : t("Mandoob:Form.Optional")}
                      )
                    </span>
                  </Label>
                  <ColumnSelect
                    headers={sheet.headers}
                    value={
                      mapping[field.key] === undefined
                        ? UNMAPPED
                        : String(mapping[field.key])
                    }
                    invalid={field.required && mapping[field.key] === undefined}
                    noneLabel={t("Mandoob:Import.NotImported")}
                    placeholder={t("Mandoob:Import.ChooseColumn")}
                    onChange={(value) => setField(field.key, value)}
                  />
                </div>
              ))}

              {/* Extra shipment facts, tucked away: open only when the file
                  already matched some, otherwise a one-line disclosure. */}
              <details
                open={extraMatched > 0 || undefined}
                className="tw:group tw:rounded-xl tw:border tw:border-border"
              >
                <summary className="tw:flex tw:cursor-pointer tw:list-none tw:items-center tw:gap-2 tw:px-3 tw:py-2.5 tw:text-sm tw:font-medium tw:text-foreground tw:[&::-webkit-details-marker]:hidden">
                  <ChevronDown className="tw:size-4 tw:shrink-0 tw:text-muted-foreground tw:transition-transform tw:group-open:rotate-180" />
                  {t("Mandoob:Import.ExtraColumns")}
                  {extraMatched > 0 ? (
                    <span className="tw:rounded-full tw:bg-brand-100 tw:px-2 tw:py-0.5 tw:text-[11px] tw:font-medium tw:text-brand-600">
                      {t("Mandoob:Import.ExtraColumnsMatched", extraMatched)}
                    </span>
                  ) : null}
                </summary>
                <div className="tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-border tw:p-3">
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    {t("Mandoob:Import.ExtraColumnsHint")}
                  </p>
                  {EXTRA_FIELDS.map((field) => (
                    <div
                      key={field.key}
                      className="tw:flex tw:flex-col tw:gap-1.5"
                    >
                      <Label>{t(FIELD_LABEL_KEYS[field.key])}</Label>
                      <ColumnSelect
                        headers={sheet.headers}
                        value={
                          mapping[field.key] === undefined
                            ? UNMAPPED
                            : String(mapping[field.key])
                        }
                        noneLabel={t("Mandoob:Import.NotImported")}
                        placeholder={t("Mandoob:Import.ChooseColumn")}
                        onChange={(value) => setField(field.key, value)}
                      />
                    </div>
                  ))}
                </div>
              </details>
            </section>

            {variableNames.length > 0 && (
              <section className="tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-border tw:pt-4">
                <div>
                  <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
                    {t("Mandoob:Import.MatchVariables")}
                  </h2>
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    {t("Mandoob:Import.MatchVariablesHint")}
                  </p>
                </div>
                {/* {ar|en}_field pairs collapse into ONE input labelled like
                    the template-preview tags; the value feeds both variants. */}
                {template &&
                  groupTemplateVariables(template.variables).map((group) => {
                    const source = variables[group.names[0]!];
                    return (
                      <div
                        key={group.key}
                        className="tw:flex tw:flex-col tw:gap-1.5"
                      >
                        <Label>
                          {group.field
                            ? t(
                                FIELD_LABEL_KEYS[
                                  IMPORT_FIELD_BY_TEMPLATE_FIELD[group.field]
                                ],
                              )
                            : `${t("Mandoob:Template.Variable")} ${group.key}`}
                        </Label>
                        <ColumnSelect
                          headers={sheet.headers}
                          value={
                            !source
                              ? UNMAPPED
                              : source.kind === "fixed"
                                ? FIXED
                                : String(source.column)
                          }
                          invalid={!source}
                          noneLabel={t("Mandoob:Import.ChooseColumn")}
                          placeholder={t("Mandoob:Import.ChooseColumn")}
                          fixedLabel={t("Mandoob:Import.FixedValue")}
                          onChange={(value) =>
                            setVariableGroup(group.names, value)
                          }
                        />
                        {source?.kind === "fixed" && (
                          <Input
                            value={source.value}
                            onChange={(event) =>
                              setVariables((prev) => {
                                const next = { ...prev };
                                for (const name of group.names) {
                                  next[name] = {
                                    kind: "fixed",
                                    value: event.target.value,
                                  };
                                }
                                return next;
                              })
                            }
                            placeholder={t("Mandoob:Import.FixedValueHint")}
                            maxLength={1024}
                          />
                        )}
                      </div>
                    );
                  })}
              </section>
            )}

            <PreviewSection
              sheet={sheet}
              valid={built.valid}
              invalid={built.invalid}
              credits={credits}
              paymentsDisabled={!!session?.appPaymentsDisabled && isNative()}
            />
          </div>
        )}

        {step === "run" && (
          <RunStep done={progress.done} total={progress.total} />
        )}

        {step === "done" && result && (
          <DoneStep
            result={result}
            fileInvalid={built.invalid.length}
            onOpen={(conversationId) =>
              void navigate({
                to: "/chats/$conversationId",
                params: { conversationId },
              })
            }
          />
        )}
      </div>

      <footer className="tw:shrink-0 tw:border-t tw:border-border">
        <div className="tw:mx-auto tw:w-full tw:p-4 tw:md:max-w-3xl">
          {step === "map" && (
            <Button
              type="button"
              onClick={() => void runImport()}
              disabled={!canImport}
              className="tw:w-full"
            >
              <Upload className="tw:size-4" />
              {t("Mandoob:Import.Submit", built.valid.length)}
            </Button>
          )}
          {step === "done" && (
            <div className="tw:flex tw:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={reset}
                className="tw:flex-1"
              >
                {t("Mandoob:Import.ImportAnother")}
              </Button>
              <Button
                type="button"
                onClick={() => void navigate({ to: "/chats" })}
                className="tw:flex-1"
              >
                {t("Mandoob:Import.GoToDeliveries")}
              </Button>
            </div>
          )}
        </div>
      </footer>

      <input
        ref={fileInput}
        type="file"
        accept={SPREADSHEET_ACCEPT}
        className="tw:hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so re-picking the same file fires change again.
          event.target.value = "";
          if (file) void handleFile(file);
        }}
      />

      <ContactsPickerDialog
        open={contactsPickerOpen}
        contacts={deviceContacts}
        onClose={() => setContactsPickerOpen(false)}
        onConfirm={applyContacts}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function FileStep({
  parsing,
  onPick,
  pickingContacts,
  contactsAvailable,
  onPickContacts,
}: {
  parsing: boolean;
  onPick: () => void;
  pickingContacts?: boolean;
  /** False on devices without a contacts source (desktop browsers). */
  contactsAvailable?: boolean;
  onPickContacts?: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <button
        type="button"
        onClick={onPick}
        disabled={parsing}
        className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-2xl tw:border-2 tw:border-dashed tw:border-border tw:px-6 tw:py-10 tw:text-center tw:transition-colors tw:hover:border-brand tw:hover:bg-brand-50/40 tw:disabled:opacity-60"
      >
        <span className="tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-2xl tw:bg-brand-50">
          {parsing ? (
            <Loader2 className="tw:size-7 tw:animate-spin tw:text-brand" />
          ) : (
            <FileSpreadsheet
              className="tw:size-7 tw:text-brand"
              strokeWidth={1.6}
            />
          )}
        </span>
        <span className="tw:text-base tw:font-semibold tw:text-foreground">
          {t("Mandoob:Import.PickFile")}
        </span>
        <span className="tw:text-xs tw:text-muted-foreground">
          {t("Mandoob:Import.PickFileHint")}
        </span>
      </button>

      <button
        type="button"
        onClick={onPickContacts}
        disabled={pickingContacts || !contactsAvailable}
        className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:border-2 tw:border-dashed tw:border-border tw:px-5 tw:py-4 tw:text-start tw:transition-colors tw:hover:border-brand tw:hover:bg-brand-50/40 tw:disabled:opacity-60"
      >
        <span className="tw:flex tw:size-11 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-xl tw:bg-brand-50">
          {pickingContacts ? (
            <Loader2 className="tw:size-5.5 tw:animate-spin tw:text-brand" />
          ) : (
            <BookUser className="tw:size-5.5 tw:text-brand" strokeWidth={1.6} />
          )}
        </span>
        <span className="tw:flex tw:min-w-0 tw:flex-col">
          <span className="tw:text-sm tw:font-semibold tw:text-foreground">
            {t("Mandoob:Import.FromContacts")}
          </span>
          <span className="tw:text-xs tw:text-muted-foreground">
            {contactsAvailable
              ? t("Mandoob:Import.FromContactsHint")
              : t("Mandoob:Import.ContactsMobileOnly")}
          </span>
        </span>
      </button>

      <div className="tw:rounded-xl tw:border tw:border-border tw:bg-muted/40 tw:p-3">
        <p className="tw:m-0 tw:text-sm tw:font-semibold">
          {t("Mandoob:Import.ExpectedColumns")}
        </p>
        <ul className="tw:m-0 tw:mt-1.5 tw:flex tw:list-disc tw:flex-col tw:gap-1 tw:ps-5 tw:text-xs tw:text-muted-foreground">
          {DELIVERY_IMPORT_FIELDS.map((field) => (
            <li key={field.key}>
              {t(FIELD_LABEL_KEYS[field.key])}
              {field.required ? ` — ${t("Mandoob:Import.Required")}` : ""}
            </li>
          ))}
        </ul>
        <p className="tw:m-0 tw:mt-2 tw:text-xs tw:text-muted-foreground">
          {t("Mandoob:Import.ExpectedColumnsHint")}
        </p>
      </div>
    </div>
  );
}

function TemplateStep({
  loading,
  templates,
  sheet,
  onSelect,
}: {
  loading: boolean;
  templates: TemplateDto[];
  sheet: SheetData | null;
  onSelect: (template: TemplateDto) => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {sheet && (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {t("Mandoob:Import.FileLoaded", sheet.fileName, sheet.rows.length)}
        </p>
      )}
      <div>
        <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
          {t("Mandoob:Import.ChooseTemplate")}
        </h2>
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {t("Mandoob:Import.ChooseTemplateHint")}
        </p>
      </div>
      {loading ? (
        <div className="tw:flex tw:justify-center tw:py-10">
          <Loader2 className="tw:size-5 tw:animate-spin tw:text-muted-foreground" />
        </div>
      ) : templates.length === 0 ? (
        <p className="tw:m-0 tw:py-10 tw:text-center tw:text-sm tw:text-muted-foreground">
          {t("Mandoob:Template.PickerEmpty")}
        </p>
      ) : (
        templates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onSelect(tpl)}
            className="tw:flex tw:flex-col tw:gap-1 tw:rounded-xl tw:border tw:border-border tw:bg-muted tw:p-3 tw:text-start tw:text-foreground tw:transition-colors tw:hover:border-foreground/20"
          >
            <span className="tw:flex tw:items-center tw:gap-2">
              <FileText className="tw:size-4 tw:shrink-0" strokeWidth={1.75} />
              <span className="tw:truncate tw:text-sm tw:font-semibold">
                {formatTemplateName(tpl.name)}
              </span>
            </span>
            {tpl.bodyText && (
              <span className="tw:line-clamp-2 tw:text-xs tw:text-muted-foreground">
                {tpl.bodyText}
              </span>
            )}
          </button>
        ))
      )}
    </div>
  );
}

function RunStep({ done, total }: { done: number; total: number }) {
  const { t } = useLanguage();
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:py-16 tw:text-center">
      <Loader2 className="tw:size-8 tw:animate-spin tw:text-brand" />
      <p className="tw:m-0 tw:text-base tw:font-semibold">
        {t("Mandoob:Import.Running", done, total)}
      </p>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className="tw:h-2 tw:w-full tw:max-w-xs tw:overflow-hidden tw:rounded-full tw:bg-muted"
      >
        <div
          className="tw:h-full tw:rounded-full tw:bg-brand tw:transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        {t("Mandoob:Import.RunningHint")}
      </p>
    </div>
  );
}

function DoneStep({
  result,
  fileInvalid,
  onOpen,
}: {
  result: ImportDeliveriesResultDto;
  fileInvalid: number;
  onOpen: (conversationId: string) => void;
}) {
  const { t } = useLanguage();
  const problems = result.results.filter((row) => row.status !== "Created");
  const created = result.results.filter((row) => row.status === "Created");

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:grid tw:grid-cols-3 tw:gap-2">
        <SummaryTile
          tone="success"
          value={result.created}
          label={t("Mandoob:Import.Created")}
        />
        <SummaryTile
          tone="danger"
          value={result.failed}
          label={t("Mandoob:Import.Failed")}
        />
        <SummaryTile
          tone="muted"
          value={result.skipped + fileInvalid}
          label={t("Mandoob:Import.Skipped")}
        />
      </div>

      {problems.length > 0 && (
        <section className="tw:flex tw:flex-col tw:gap-2">
          <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
            {t("Mandoob:Import.ProblemRows")}
          </h2>
          <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1.5 tw:p-0">
            {problems.map((row) => (
              <li
                key={row.rowNumber}
                className="tw:flex tw:items-start tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:p-2.5"
              >
                {row.status === "Failed" ? (
                  <XCircle className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-destructive" />
                ) : (
                  <AlertTriangle className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                )}
                <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                  <span className="tw:truncate tw:text-sm tw:font-medium">
                    {t("Mandoob:Import.RowLabel", row.rowNumber)} ·{" "}
                    {row.clientName ??
                      formatPhoneNumberForDisplay(row.clientPhone)}
                  </span>
                  <span className="tw:text-xs tw:text-muted-foreground">
                    {serverErrorText(row, t)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {created.length > 0 && (
        <section className="tw:flex tw:flex-col tw:gap-2">
          <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
            {t("Mandoob:Import.CreatedRows")}
          </h2>
          <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1.5 tw:p-0">
            {created.map((row) => (
              <li key={row.rowNumber}>
                <button
                  type="button"
                  onClick={() =>
                    row.conversationId && onOpen(row.conversationId)
                  }
                  className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:p-2.5 tw:text-start tw:transition-colors tw:hover:bg-muted/60"
                >
                  <CheckCircle2 className="tw:size-4 tw:shrink-0 tw:text-brand" />
                  <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm">
                    {row.clientName ??
                      formatPhoneNumberForDisplay(row.clientPhone)}
                  </span>
                  <span
                    className="tw:shrink-0 tw:text-xs tw:text-muted-foreground"
                    dir="ltr"
                    style={{ unicodeBidi: "isolate" }}
                  >
                    {formatPhoneNumberForDisplay(row.clientPhone)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function FileSummary({
  sheet,
  template,
  onChangeFile,
  onChangeTemplate,
}: {
  sheet: SheetData;
  template: TemplateDto | null;
  onChangeFile: () => void;
  onChangeTemplate: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:rounded-xl tw:border tw:border-border tw:bg-muted/40 tw:p-3">
      <div className="tw:flex tw:items-center tw:gap-2">
        <FileSpreadsheet className="tw:size-4 tw:shrink-0 tw:text-brand" />
        <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-medium">
          {sheet.fileName}
        </span>
        <button
          type="button"
          onClick={onChangeFile}
          className="tw:shrink-0 tw:text-xs tw:font-medium tw:text-brand tw:underline"
        >
          {t("Mandoob:Import.ChangeFile")}
        </button>
      </div>
      {template && (
        <div className="tw:flex tw:items-center tw:gap-2">
          <FileText className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm">
            {formatTemplateName(template.name)}
          </span>
          <button
            type="button"
            onClick={onChangeTemplate}
            className="tw:shrink-0 tw:text-xs tw:font-medium tw:text-brand tw:underline"
          >
            {t("Mandoob:Import.ChangeTemplate")}
          </button>
        </div>
      )}
    </div>
  );
}

function ColumnSelect({
  headers,
  value,
  onChange,
  noneLabel,
  placeholder,
  fixedLabel,
  invalid,
}: {
  headers: string[];
  value: string;
  onChange: (value: string) => void;
  noneLabel: string;
  placeholder: string;
  fixedLabel?: string;
  invalid?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-invalid={invalid || undefined}
        className="tw:w-full tw:justify-between"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNMAPPED}>{noneLabel}</SelectItem>
        {headers.map((header, index) => (
          <SelectItem key={index} value={String(index)}>
            {header}
          </SelectItem>
        ))}
        {fixedLabel && <SelectItem value={FIXED}>{fixedLabel}</SelectItem>}
      </SelectContent>
    </Select>
  );
}

function PreviewSection({
  sheet,
  valid,
  invalid,
  credits,
  paymentsDisabled,
}: {
  sheet: SheetData;
  valid: ImportDeliveryRowInput[];
  invalid: InvalidRow[];
  credits: number;
  /** Verification builds: no credit-cost banner (server still enforces). */
  paymentsDisabled?: boolean;
}) {
  const { t } = useLanguage();
  // Free-messages offer: every row's template is that shipment's first free send.
  const cost = isFreeMessagesOfferActive()
    ? 0
    : valid.length * TEMPLATE_SEND_COST;
  const shortOnCredits = !paymentsDisabled && cost > credits;

  return (
    <section className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:pt-4">
      <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
        {t("Mandoob:Import.Preview")}
      </h2>
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        {t("Mandoob:Import.ReadyCount", valid.length, sheet.rows.length)}
      </p>

      {!paymentsDisabled && (
        <div
          className={cn(
            "tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:p-2.5 tw:text-xs",
            shortOnCredits
              ? "tw:border-destructive/40 tw:bg-destructive/5 tw:text-destructive"
              : "tw:border-border tw:text-muted-foreground",
          )}
        >
          <Coins className="tw:size-4 tw:shrink-0" strokeWidth={1.75} />
          <span>
            {shortOnCredits
              ? t("Mandoob:Import.NotEnoughCredits", cost, credits)
              : t("Mandoob:Import.CreditCost", cost, credits)}
          </span>
        </div>
      )}

      {valid.length > 0 && (
        <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1.5 tw:p-0">
          {valid.slice(0, 3).map((row) => (
            <li
              key={row.rowNumber}
              className="tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:p-2.5"
            >
              <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm">
                {row.clientName ?? formatPhoneNumberForDisplay(row.clientPhone)}
              </span>
              {row.trackingNumber && (
                <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
                  {row.trackingNumber}
                </span>
              )}
              <span
                className="tw:shrink-0 tw:text-xs tw:text-muted-foreground"
                dir="ltr"
                style={{ unicodeBidi: "isolate" }}
              >
                {formatPhoneNumberForDisplay(row.clientPhone)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {invalid.length > 0 && (
        <details className="tw:rounded-lg tw:border tw:border-border tw:p-2.5">
          <summary className="tw:cursor-pointer tw:text-sm tw:font-medium tw:text-destructive">
            {t("Mandoob:Import.SkippedRows", invalid.length)}
          </summary>
          <ul className="tw:m-0 tw:mt-2 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0">
            {invalid.slice(0, 30).map((row) => (
              <li
                key={row.rowNumber}
                className="tw:text-xs tw:text-muted-foreground"
              >
                {t("Mandoob:Import.RowLabel", row.rowNumber)} · {row.label} —{" "}
                {invalidReasonText(row.reason, t)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function SummaryTile({
  tone,
  value,
  label,
}: {
  tone: "success" | "danger" | "muted";
  value: number;
  label: string;
}) {
  return (
    <div
      className={cn(
        "tw:flex tw:flex-col tw:items-center tw:gap-0.5 tw:rounded-xl tw:border tw:p-3",
        tone === "success" && "tw:border-brand-200 tw:bg-brand-50",
        tone === "danger" && "tw:border-destructive/40 tw:bg-destructive/5",
        tone === "muted" && "tw:border-border tw:bg-muted/40",
      )}
    >
      <span className="tw:text-xl tw:font-semibold">{value}</span>
      <span className="tw:text-center tw:text-xs tw:text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Rows the run never reached (credits ran out, or a request failed) are folded
 * in as Skipped so the summary accounts for every submitted row.
 */
function summarize(
  submitted: ImportDeliveryRowInput[],
  results: DeliveryImportRowResultDto[],
  stopCode: string | null,
): ImportDeliveriesResultDto {
  const seen = new Set(results.map((row) => row.rowNumber));
  const all = [...results];
  for (const row of submitted) {
    if (seen.has(row.rowNumber)) continue;
    all.push({
      rowNumber: row.rowNumber,
      clientPhone: row.clientPhone,
      clientName: row.clientName ?? null,
      status: "Skipped",
      conversationId: null,
      errorCode: stopCode,
      errorMessage: null,
    });
  }
  all.sort((a, b) => a.rowNumber - b.rowNumber);
  return {
    created: all.filter((row) => row.status === "Created").length,
    failed: all.filter((row) => row.status === "Failed").length,
    skipped: all.filter((row) => row.status === "Skipped").length,
    results: all,
  };
}

function serverErrorText(
  row: DeliveryImportRowResultDto,
  t: TranslateFn,
): string {
  // Row results carry a bare code; unmapped ones fall back to a translated
  // line rather than the API English message.
  return messageForErrorCode(
    row.errorCode,
    t,
    row.status === "Skipped"
      ? "Mandoob:Import.NotAttempted"
      : "AnErrorOccurred",
  );
}

function invalidReasonText(reason: InvalidRowReason, t: TranslateFn): string {
  switch (reason.kind) {
    case "MissingPhone":
      return t("Mandoob:Import.ReasonMissingPhone");
    case "InvalidPhone":
      return t("Mandoob:Import.ReasonInvalidPhone", reason.value);
    case "DuplicatePhone":
      return t("Mandoob:Import.ReasonDuplicatePhone");
    case "InvalidDriverPhone":
      return t("Mandoob:Import.ReasonInvalidDriverPhone", reason.value);
    case "MissingVariable":
      return t("Mandoob:Import.ReasonMissingVariable", reason.variable);
  }
}
